import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { QualityTestDefinitionsService } from '../quality-test-definitions.service';
import { QualityTestDefinition } from '../quality-test-definition';
import { QualityTestFieldRegistry } from '../quality-test-field-registry';
import { QualityTestInterpretResult } from '../quality-test-interpret-result';
import { AlertService } from '@app/_services';

@Component({
  selector: 'app-quality-test-definition-form',
  templateUrl: './quality-test-definition-form.component.html',
  styleUrls: ['./quality-test-definition-form.component.css'],
  standalone: false
})
export class QualityTestDefinitionFormComponent implements OnInit {

  form: UntypedFormGroup;
  activeTab = 'guided';
  formLoading = false;

  interpretText = '';
  interpreting = false;
  interpretResult: QualityTestInterpretResult | null = null;

  fields: QualityTestFieldRegistry[] = [];

  severityOptions = [
    { label: 'FAIL', value: 'FAIL' },
    { label: 'WARN', value: 'WARN' },
    { label: 'INFO', value: 'INFO' },
  ];

  categoryOptions = [
    { label: 'LISTING', value: 'LISTING' },
    { label: 'RATES', value: 'RATES' },
    { label: 'FEES', value: 'FEES' },
    { label: 'PHOTOS', value: 'PHOTOS' },
    { label: 'AMENITIES', value: 'AMENITIES' },
    { label: 'CONTENT', value: 'CONTENT' },
    { label: 'OTHER', value: 'OTHER' },
  ];

  ruleTypeOptions = [
    { label: 'PARAMETRIC', value: 'PARAMETRIC' },
    { label: 'AI_EVAL', value: 'AI_EVAL' },
  ];

  operatorOptions = [
    { label: 'GTE (>=)', value: 'GTE' },
    { label: 'LTE (<=)', value: 'LTE' },
    { label: 'GT (>)', value: 'GT' },
    { label: 'LT (<)', value: 'LT' },
    { label: 'EQ (=)', value: 'EQ' },
    { label: 'NEQ (!=)', value: 'NEQ' },
    { label: 'NOT_NULL', value: 'NOT_NULL' },
    { label: 'NOT_BLANK', value: 'NOT_BLANK' },
    { label: 'MIN_LENGTH', value: 'MIN_LENGTH' },
  ];

  constructor(
    public dialogRef: MatDialogRef<QualityTestDefinitionFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: QualityTestDefinition | null,
    private fb: UntypedFormBuilder,
    private service: QualityTestDefinitionsService,
    private alertService: AlertService,
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.buildForm();
    this.loadFields();

    if (this.data) {
      this.form.patchValue(this.data);
    }
  }

  buildForm() {
    this.form = this.fb.group({
      testDefinitionId: [null],
      testName: ['', Validators.required],
      testCategory: [''],
      testDisplayName: [''],
      testExpectationDesc: [''],
      severity: ['WARN', Validators.required],
      scoreWeight: [1],
      ruleType: ['PARAMETRIC'],
      ruleField: [''],
      ruleOperator: [''],
      ruleThreshold: [''],
      aiEvalPrompt: [''],
      aiEvalModel: [''],
      appliesToIsland: [''],
      appliesToChannel: [''],
      isActive: [1],
      sortOrder: [0],
      notes: [''],
    });
  }

  loadFields() {
    this.service.findAllFields().pipe(first()).subscribe(
      (res: any) => {
        this.fields = res.data?.qualityTestFieldRegistry || [];
      },
      (err: any) => { }
    );
  }

  get fieldOptions() {
    if (!Array.isArray(this.fields)) return [];
    return this.fields.map(f => ({ label: f.displayName || f.fieldKey, value: f.fieldKey }));
  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  onInterpret() {
    if (!this.interpretText.trim()) return;
    this.interpreting = true;
    this.interpretResult = null;
    this.service.interpret(this.interpretText).pipe(first()).subscribe(
      (res: any) => {
        this.interpretResult = (res.data?.interpretation ?? res.data) as QualityTestInterpretResult;
      },
      (err: any) => {
        this.alertService.error(err);
      },
      () => { this.interpreting = false; }
    );
  }

  applyResult() {
    if (!this.interpretResult) return;
    const patch: any = {};
    if (this.interpretResult.ruleType) patch.ruleType = this.interpretResult.ruleType;
    if (this.interpretResult.ruleField) patch.ruleField = this.interpretResult.ruleField;
    if (this.interpretResult.ruleOperator) patch.ruleOperator = this.interpretResult.ruleOperator;
    if (this.interpretResult.ruleThreshold) patch.ruleThreshold = this.interpretResult.ruleThreshold;
    if (this.interpretResult.aiEvalPrompt) patch.aiEvalPrompt = this.interpretResult.aiEvalPrompt;
    this.form.patchValue(patch);
    this.activeTab = 'guided';
  }

  getResolutionSeverity(resolution: string): string {
    if (resolution === 'PARAMETRIC') return 'success';
    if (resolution === 'AI_EVAL') return 'warning';
    return 'danger';
  }

  onSave() {
    if (this.form.invalid) return;
    this.formLoading = true;
    const def: QualityTestDefinition = this.form.value;
    const isEdit = !!def.testDefinitionId;
    const call = isEdit ? this.service.update(def) : this.service.add(def);

    call.pipe(first()).subscribe(
      (res: any) => {
        this.dialogRef.close(res.data);
      },
      (err: any) => {
        this.alertService.error(err);
        this.formLoading = false;
      },
      () => { this.formLoading = false; }
    );
  }

  onCancel() {
    this.dialogRef.close(null);
  }
}
